/*global Rollbar */
import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useClockDrift, getDiagnostics } from '../../utilities/clockDrift';

/**
 * Gather client information for debugging
 */
function getClientInfo() {
  const nav = navigator || {};
  const screen = window.screen || {};
  const connection =
    nav.connection || nav.mozConnection || nav.webkitConnection || {};

  return {
    // Time info
    clientTime: new Date().toISOString(),
    clientTimestamp: Date.now(),
    timezoneOffset: new Date().getTimezoneOffset(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,

    // Browser info
    userAgent: nav.userAgent,
    platform: nav.platform,
    vendor: nav.vendor,
    language: nav.language,
    languages: nav.languages,
    cookieEnabled: nav.cookieEnabled,
    onLine: nav.onLine,

    // Screen info
    screenWidth: screen.width,
    screenHeight: screen.height,
    screenAvailWidth: screen.availWidth,
    screenAvailHeight: screen.availHeight,
    colorDepth: screen.colorDepth,
    pixelRatio: window.devicePixelRatio,

    // Viewport
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,

    // Connection info
    connectionType: connection.type,
    connectionEffectiveType: connection.effectiveType,
    connectionDownlink: connection.downlink,
    connectionRtt: connection.rtt,

    // URL info
    url: window.location.href,
    pathname: window.location.pathname,
    search: window.location.search,
  };
}

/**
 * Clock drift monitor component.
 *
 * This component does NOT display any UI warnings because:
 * - ADMIN/PRODUCER clock behind: auto-compensated in scoring.js
 * - ADMIN/PRODUCER clock ahead: not a problem (scores are in the past)
 * - JUDGE clock: doesn't affect scoring (createdAt is server-generated)
 *
 * Instead, it silently reports significant drift (>10s) to Rollbar
 * for debugging and monitoring purposes.
 */
export default function ClockDriftWarning() {
  const [hasReported, setHasReported] = useState(false);
  const producerLive = useSelector((state) => state.producer?.live);
  const sessionId = useSelector((state) => state.session?.id);
  const sessionName = useSelector((state) => state.session?.name);
  const sessionRole = useSelector((state) => state.session?.role);
  const prevLiveRef = useRef(producerLive);

  // Use role-aware drift info
  const driftInfo = useClockDrift(sessionRole);

  // Reset reporting flag when scoring starts (live goes from false to true)
  useEffect(() => {
    if (producerLive && !prevLiveRef.current) {
      setHasReported(false);
    }
    prevLiveRef.current = producerLive;
  }, [producerLive]);

  // Check if drift is auto-compensated (ADMIN/PRODUCER behind)
  const isCompensatable =
    ['ADMIN', 'PRODUCER'].includes(sessionRole) &&
    driftInfo.drift !== null &&
    driftInfo.drift < 0;

  // Only report to Rollbar for significant drift (>10s) to avoid noise
  const rollbarReportThreshold = 10000; // 10 seconds
  const hasSignificantDrift =
    driftInfo.drift !== null &&
    Math.abs(driftInfo.drift) > rollbarReportThreshold;

  // Report to Rollbar when significant drift is detected (>10s)
  useEffect(() => {
    if (hasSignificantDrift && !hasReported) {
      setHasReported(true);

      const clientInfo = getClientInfo();
      const diagnostics = getDiagnostics();

      // Use 'warning' level for compensated drift, 'error' for uncompensated
      const rollbarLevel = isCompensatable ? 'warning' : 'error';
      const rollbarMessage = isCompensatable
        ? 'Significant clock drift detected (auto-compensated)'
        : 'Significant clock drift detected';

      Rollbar[rollbarLevel](rollbarMessage, {
        drift: {
          driftMs: driftInfo.drift,
          driftSeconds: (driftInfo.drift / 1000).toFixed(1),
          direction: driftInfo.direction,
          status: driftInfo.status,
          message: driftInfo.message,
          isCalibrated: driftInfo.isCalibrated,
          isCompensatable,
          calibratedDrift: driftInfo.calibratedDrift,
          rawDrift: driftInfo.rawDrift,
          lastRtt: driftInfo.lastRtt,
          calibrationQuality: driftInfo.calibrationQuality,
          calibrationVariance: driftInfo.calibrationVariance,
          isProblematicDirection: driftInfo.isProblematicDirection,
          lastServerTime: driftInfo.lastServerTime
            ? new Date(driftInfo.lastServerTime).toISOString()
            : null,
          lastClientTime: driftInfo.lastClientTime
            ? new Date(driftInfo.lastClientTime).toISOString()
            : null,
        },
        calibration: {
          samples: diagnostics.calibrationSamples,
          thresholds: diagnostics.thresholds,
          quality: diagnostics.calibrationQuality,
          variance: diagnostics.calibrationVariance,
        },
        session: {
          id: sessionId,
          name: sessionName,
          role: sessionRole,
          producerLive,
        },
        client: clientInfo,
      });
    }
  }, [
    hasSignificantDrift,
    hasReported,
    isCompensatable,
    driftInfo,
    sessionId,
    sessionName,
    sessionRole,
    producerLive,
  ]);

  // No UI - this component only reports to Rollbar
  return null;
}
